import { createStore } from "~/lib/store/storeFactory";
import type {
  TenantInfoFormData,
  CustomerAdminFormData,
} from "../utils/formatters";
import type { AccountManagerFormData } from "~/Pages/Tenants/types/tenants";

/**
 * Tenant onboarding store state
 */
export interface TenantOnboardingState {
  activeStep: 0 | 1;
  step1Data: TenantInfoFormData;
  step2Data: CustomerAdminFormData;
  accountManagerData: AccountManagerFormData;
  adminInformation: CustomerAdminFormData | null;
  accountManagerInformation: AccountManagerFormData | null;
  isDirty: boolean;
  validationErrors: Record<string, string[]>;
  touchedFields: Set<string>; // Track which fields have been touched/blurred
  organizationId: string | null;
  generalInformation: TenantInfoFormData | null;
  isEditing: boolean;
  domainStatus: object;
  isSuccess: boolean;
  successTenantId: string | null;
  successTenantName: string | null;
}

/**
 * Initial state for tenant onboarding
 */
const initialState: TenantOnboardingState = {
  activeStep: 0,
  step1Data: {
    tenant_name: "",
    type: "CUSTOMER",
    demo_tenant: false,
    account_name: "",
    domains: [],
    total_number_of_users: null,
    salesforce_id: "",
    country: "",
    status: "ACTIVE",
    billing_start_date: null,
    billing_end_date: null,
    supports_payroll_plus: null,
    payslip_portal_link: "",
  },
  step2Data: {
    given_name: "",
    surname: "",
    other_name: "",
    preferred_name: "",
    contact_email: "",
    country: "",
    phone_code: "",
    phone_number: "",
  },
  accountManagerData: {
    account_manager_id: "",
    account_manager_email: "",
    is_new_account_manager: false,
  },
  adminInformation: null,
  accountManagerInformation: null,
  generalInformation: null,
  isDirty: false,
  validationErrors: {},
  touchedFields: new Set<string>(),
  organizationId: null,
  isEditing: false,
  domainStatus: {},
  isSuccess: false,
  successTenantId: null,
  successTenantName: null,
};

/**
 * Create the tenant onboarding store
 */
export const tenantOnboardingStore =
  createStore<TenantOnboardingState>(initialState);

/**
 * Store actions
 */
export const tenantOnboardingActions = {
  /**
   * Update Step 1 data
   */
  updateStep1Data: (updates: Partial<TenantInfoFormData>) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      step1Data: { ...state.step1Data, ...updates },
      isDirty: true,
    }));
  },

  /**
   * Update Step 2 data
   */
  updateStep2Data: (updates: Partial<CustomerAdminFormData>) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      step2Data: { ...state.step2Data, ...updates },
      isDirty: true,
      validationErrors: {}, // Clear errors when updating
    }));
  },

  /**
   * Update Account Manager data
   */
  updateAccountManagerData: (updates: Partial<AccountManagerFormData>) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      accountManagerData: { ...state.accountManagerData, ...updates },
      isDirty: true,
      validationErrors: {}, // Clear errors when updating
    }));
  },

  /**
   * Set active step
   */
  setActiveStep: (step: 0 | 1) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      activeStep: step,
    }));
  },

  /**
   * Set validation errors
   */
  setValidationErrors: (errors: Record<string, string[]>) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      validationErrors: errors,
    }));
  },

  /**
   * Clear validation error for a specific field
   */
  clearFieldError: (fieldName: string) => {
    tenantOnboardingStore.setState((state) => {
      const newErrors = { ...state.validationErrors };
      delete newErrors[fieldName];
      return {
        ...state,
        validationErrors: newErrors,
      };
    });
  },

  /**
   * Mark a field as touched (user has interacted with it)
   */
  markFieldTouched: (fieldName: string) => {
    tenantOnboardingStore.setState((state) => {
      const newTouchedFields = new Set(state.touchedFields);
      newTouchedFields.add(fieldName);
      return {
        ...state,
        touchedFields: newTouchedFields,
      };
    });
  },

  /**
   * Clear all touched fields
   */
  clearTouchedFields: () => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      touchedFields: new Set<string>(),
    }));
  },

  /**
   * Set isDirty flag
   */
  setIsDirty: (isDirty: boolean) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      isDirty,
    }));
  },

  /**
   * Set organization ID
   */
  setOrganizationId: (organizationId: string | null) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      organizationId,
    }));
  },

  /**
   * Set success state
   */
  setSuccess: (tenantId: string, tenantName: string) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      isSuccess: true,
      successTenantId: tenantId,
      successTenantName: tenantName,
      isDirty: false, // Mark as not dirty to prevent leave confirmation
    }));
  },

  /**
   * Clear success state
   */
  clearSuccess: () => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      isSuccess: false,
      successTenantId: null,
      successTenantName: null,
    }));
  },

  /**
   * Reset store to initial state (preserves organizationId)
   */
  reset: () => {
    tenantOnboardingStore.setState((state) => ({
      ...initialState,
      touchedFields: new Set<string>(),
      organizationId: state.organizationId, // Preserve organizationId
      isSuccess: false,
      successTenantId: null,
      successTenantName: null,
    }));
  },

  setStep1Data: (data: TenantInfoFormData) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      step1Data: data,
      generalInformation: JSON.parse(JSON.stringify(data)), // Deep copy to prevent shared reference
      isDirty: false,
      validationErrors: {},
      touchedFields: new Set(),
    }));
  },

  setStep2Data: (data: CustomerAdminFormData) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      step2Data: data,
      adminInformation: data,
      isDirty: false,
      validationErrors: {},
      touchedFields: new Set(),
    }));
  },

  setAccountManagerData: (data: AccountManagerFormData) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      accountManagerData: data,
      accountManagerInformation: data,
      isDirty: false,
      validationErrors: {},
      touchedFields: new Set(),
    }));
  },

  setIsEditing: (isEditing: boolean) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      isEditing,
    }));
  },

  setGeneralInformation: (data: TenantInfoFormData) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      generalInformation: data,
    }));
  },

  setAdminInformation: (data: CustomerAdminFormData) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      adminInformation: data,
    }));
  },

  setAccountManagerInformation: (data: AccountManagerFormData) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      accountManagerInformation: data,
    }));
  },
  setDomainStatus: (domain: object) => {
    tenantOnboardingStore.setState((state) => ({
      ...state,
      domainStatus: domain,
    }));
  },
};
